// src/components/SEO.jsx — Phase 4 W4.T1.
//
// Per-route SEO meta wrapper. Wraps react-helmet-async <Helmet> so each
// route can override title / description / OG / Twitter / canonical /
// JSON-LD without having to import the library individually.
//
// Defaults mirror public/index.html so a route that supplies no props
// still emits a sane <head>. Per-route callers (the legal pages) override
// with route-specific values.
import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE = "https://puzzly.giadalabs.com";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.png`;
const DEFAULT_TITLE = "Puzzly — A Puzzle Made Just for You";
const DEFAULT_DESCRIPTION =
  "Create a personalized puzzle for someone you love. They solve the riddles to unlock your surprise message.";
const DEFAULT_CANONICAL = "https://puzzly.giadalabs.com/";

// Map i18n language → localized OG card + OpenGraph locale tag.
const OG_LOCALE = { en: "en_US", it: "it_IT", es: "es_ES", fr: "fr_FR" };

export default function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonical = DEFAULT_CANONICAL,
  ogImage,
  jsonLd = null,
}) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || "en";
  // When the caller doesn't force a specific image, serve the per-language
  // share card (og-image-<lang>.png). Falls back to the English default.
  const resolvedOg =
    ogImage || `${SITE}/og-image-${OG_LOCALE[lang] ? lang : "en"}.png`;
  const ogLocale = OG_LOCALE[lang] || "en_US";

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={resolvedOg} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Puzzly" />
      <meta property="og:locale" content={ogLocale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedOg} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
